/**
 * intakeTrModel.ts
 *
 * Pure logic for the Title Search Intake Mode: field metadata, validation
 * summary, per-file status and the primary action label. No React, no I/O.
 *
 * Validation delegates to `titleSearch/createJob.ts` (required fields, lengths,
 * effective-date window) and `titleAnalysis/inputs.ts` (Subject loan limits), so
 * the rules stay in step with the backend `CreateTitleJobInput` and
 * `TitleAnalysisInputs` models.
 *
 * Requirements: 6.1, 6.2, 6.3, 6.4, 6.5, 6.6, 6.7.
 */

import { FileStatus, type WorkflowJob } from '../../graphql/types';
import {
  EMPTY_TITLE_SEARCH_FORM,
  validateTitleSearchJobForm,
  type TitleSearchFormErrors,
  type TitleSearchJobFormState,
} from '../../titleSearch/createJob';
import {
  validateTitleAnalysisInputs,
  type TitleAnalysisInputs,
} from '../../titleAnalysis/inputs';
import { isTitleReviewFinalized } from '../rowMode';

// ---------------------------------------------------------------------------
// Search order (required) fields
// ---------------------------------------------------------------------------

export interface SearchOrderField {
  key: keyof TitleSearchJobFormState;
  label: string;
  placeholder?: string;
  type?: 'text' | 'date';
  /** Spans both columns of the field grid. */
  wide?: boolean;
}

/** Required Search order fields, in display order (Requirement 6.1). */
export const SEARCH_ORDER_FIELDS: readonly SearchOrderField[] = [
  { key: 'loanNumber', label: 'Loan number', placeholder: 'e.g. LN-1042' },
  { key: 'clientName', label: 'Client', placeholder: 'Who ordered the search' },
  { key: 'borrowerName', label: "Borrower's name", placeholder: 'Name as it appears of record' },
  { key: 'effectiveDate', label: 'Effective date', type: 'date' },
  { key: 'propertyAddress', label: 'Property address', placeholder: 'Street address', wide: true },
  { key: 'propertyCounty', label: 'County', placeholder: 'e.g. Miami-Dade' },
  { key: 'propertyState', label: 'State', placeholder: 'e.g. FL' },
];

/** Readable names for the required fields, for messages. */
export const SEARCH_ORDER_LABELS: Readonly<Record<keyof TitleSearchJobFormState, string>> =
  Object.fromEntries(SEARCH_ORDER_FIELDS.map((f) => [f.key, f.label])) as Record<
    keyof TitleSearchJobFormState,
    string
  >;

// ---------------------------------------------------------------------------
// Subject loan (optional) fields; match `TitleAnalysisInputs`
// ---------------------------------------------------------------------------

/** String-valued Subject loan state. Every field is optional. */
export interface SubjectLoanState {
  useCase: string;
  vestingEntity: string;
  guarantorNames: string;
  loanAmount: string;
  expectedLienPosition: string;
  currentLoanHolder: string;
  noteDate: string;
  subjectMortgageRecordingReference: string;
}

export const EMPTY_SUBJECT_LOAN: SubjectLoanState = {
  useCase: '',
  vestingEntity: '',
  guarantorNames: '',
  loanAmount: '',
  expectedLienPosition: '',
  currentLoanHolder: '',
  noteDate: '',
  subjectMortgageRecordingReference: '',
};

/** Optional search coverage: where the search starts and which indexes were searched. */
export interface SearchCoverageState {
  searchStartDate: string;
  indexesSearched: string[];
}

export const EMPTY_SEARCH_COVERAGE: SearchCoverageState = {
  searchStartDate: '',
  indexesSearched: [],
};

/** Expected lien position options (same values as the legacy create form). */
export const LIEN_POSITION_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '1', label: '1st lien' },
  { value: '2', label: '2nd lien' },
  { value: '3', label: '3rd lien' },
];

/** The whole Intake form: Search order, optional APN, Subject loan and Search coverage. */
export interface TrIntakeForm {
  order: TitleSearchJobFormState;
  apn: string;
  subject: SubjectLoanState;
  coverage: SearchCoverageState;
}

export const EMPTY_TR_INTAKE_FORM: TrIntakeForm = {
  order: { ...EMPTY_TITLE_SEARCH_FORM },
  apn: '',
  subject: { ...EMPTY_SUBJECT_LOAN },
  coverage: { ...EMPTY_SEARCH_COVERAGE, indexesSearched: [] },
};

/** `TitleAnalysisInputs` view of the form, for validation and the create payload. */
export function toTitleAnalysisInputs(form: TrIntakeForm): TitleAnalysisInputs {
  return {
    useCase: form.subject.useCase,
    vestingEntity: form.subject.vestingEntity,
    guarantorNames: form.subject.guarantorNames,
    loanAmount: form.subject.loanAmount,
    noteDate: form.subject.noteDate,
    searchStartDate: form.coverage.searchStartDate,
    indexesSearched: form.coverage.indexesSearched,
    subjectMortgageRecordingReference: form.subject.subjectMortgageRecordingReference,
    expectedLienPosition: form.subject.expectedLienPosition,
    currentLoanHolder: form.subject.currentLoanHolder,
    apn: form.apn,
  };
}

/** Prefill the form from an existing search (read-only display). */
export function formFromJob(job: WorkflowJob): TrIntakeForm {
  const s = (value: string | null | undefined) => value ?? '';
  return {
    order: {
      loanNumber: s(job.loanNumber),
      clientName: s(job.clientName),
      borrowerName: s(job.borrowerName),
      effectiveDate: s(job.effectiveDate),
      propertyAddress: s(job.propertyAddress),
      propertyCounty: s(job.propertyCounty),
      propertyState: s(job.propertyState),
    },
    apn: s(job.apn),
    subject: {
      useCase: s(job.useCase),
      vestingEntity: s(job.vestingEntity),
      guarantorNames: s(job.guarantorNames),
      loanAmount: job.subjectLoanAmount == null ? '' : String(job.subjectLoanAmount),
      expectedLienPosition: s(job.expectedLienPosition),
      currentLoanHolder: s(job.currentLoanHolder),
      noteDate: s(job.noteDate),
      subjectMortgageRecordingReference: s(job.subjectMortgageRecordingReference),
    },
    coverage: {
      searchStartDate: s(job.searchStartDate),
      indexesSearched: job.indexesSearched ? [...job.indexesSearched] : [],
    },
  };
}

// ---------------------------------------------------------------------------
// Validation summary
// ---------------------------------------------------------------------------

export interface IntakeValidation {
  /** Per-field errors for the required Search order fields. */
  orderErrors: TitleSearchFormErrors;
  /** Per-field errors for the optional Subject loan and APN fields. */
  analysisErrors: Partial<Record<keyof TitleAnalysisInputs, string>>;
  /** Required fields that are blank, in display order. */
  missing: Array<keyof TitleSearchJobFormState>;
  /** Count of required fields that are blank (Requirement 6.4). */
  missingCount: number;
  /** Fields that are filled but invalid (e.g. effective date out of range). */
  invalidCount: number;
  /** True when at least one recorded document is attached. */
  hasDocuments: boolean;
  /** True when the search can be created. */
  valid: boolean;
}

/**
 * Validate the whole Intake form.
 *
 * A required field is "missing" when it is blank after trimming. Other
 * failures (length, effective-date window, Subject loan limits) are counted
 * as invalid so the summary can name both. The effective date window comes from
 * `effectiveDateBounds` through `validateTitleSearchJobForm` (Requirement 6.5).
 */
export function validateIntake(
  form: TrIntakeForm,
  documentCount: number,
  today: Date = new Date()
): IntakeValidation {
  const orderErrors = validateTitleSearchJobForm(form.order, { today });
  const missing = SEARCH_ORDER_FIELDS.map((f) => f.key).filter(
    (key) => !form.order[key].trim()
  );

  const rawAnalysis = validateTitleAnalysisInputs(toTitleAnalysisInputs(form));
  const analysisErrors: IntakeValidation['analysisErrors'] = {};
  for (const [key, message] of Object.entries(rawAnalysis)) {
    if (message) analysisErrors[key as keyof TitleAnalysisInputs] = message;
  }

  const invalidOrder = Object.keys(orderErrors).filter(
    (key) => !missing.includes(key as keyof TitleSearchJobFormState)
  ).length;
  const invalidCount = invalidOrder + Object.keys(analysisErrors).length;
  const hasDocuments = documentCount > 0;

  return {
    orderErrors,
    analysisErrors,
    missing,
    missingCount: missing.length,
    invalidCount,
    hasDocuments,
    valid: missing.length === 0 && invalidCount === 0 && hasDocuments,
  };
}

/** "1 required field missing" / "3 required fields missing". */
export function formatMissingCount(count: number): string {
  return `${count} required ${count === 1 ? 'field' : 'fields'} missing`;
}

/** "2 fields need a fix" / "1 field needs a fix". */
export function formatInvalidCount(count: number): string {
  return count === 1 ? '1 field needs a fix' : `${count} fields need a fix`;
}

// ---------------------------------------------------------------------------
// Uploaded recorded documents
// ---------------------------------------------------------------------------

export type IntakeFileStatus = 'ready' | 'processing' | 'processed' | 'failed';

/** Per-file labels (Requirement 6.6). */
export const FILE_STATUS_LABELS: Readonly<Record<IntakeFileStatus, string>> = {
  ready: 'Ready to process',
  processing: 'Processing',
  processed: 'Processed',
  failed: 'Failed',
};

/**
 * Status of a file already stored on the search.
 *
 * - a recorded error, or a failed job step, is Failed
 * - Review required / completed are Processed
 * - Processing is Processing
 * - Created is Processing once the job is submitted, otherwise Ready to process
 */
export function getStoredFileStatus(
  file: { status?: FileStatus | string | null; errorMessage?: string | null },
  jobProcessing: boolean
): IntakeFileStatus {
  if (file.errorMessage) return 'failed';
  switch (file.status) {
    case FileStatus.REVIEW_REQUIRED:
    case FileStatus.REVIEW_COMPLETED:
      return 'processed';
    case FileStatus.PROCESSING:
      return 'processing';
    case FileStatus.CREATED:
    default:
      return jobProcessing ? 'processing' : 'ready';
  }
}

/** True while the job's Document Processing step is running. */
export function isJobProcessing(job: Pick<WorkflowJob, 'documentProcessingStatus'> | null): boolean {
  return (job?.documentProcessingStatus ?? '').toString().toLowerCase() === 'in_progress';
}

/** Formats supported by the document pipeline (same set as Document Processing). */
const SUPPORTED_EXTENSIONS = new Set(['pdf', 'png', 'jpg', 'jpeg', 'tiff', 'tif', 'bmp', 'gif']);
export const SUPPORTED_FORMATS_LABEL = 'PDF, PNG, JPG, TIFF, BMP or GIF';
export const ACCEPTED_FILE_TYPES = '.pdf,.png,.jpg,.jpeg,.tiff,.tif,.bmp,.gif';

/** Returns an error message for an unsupported file name, or null when accepted. */
export function validateRecordedDocumentName(name: string): string | null {
  const dot = name.lastIndexOf('.');
  const ext = dot >= 0 ? name.slice(dot + 1).toLowerCase() : '';
  if (!SUPPORTED_EXTENSIONS.has(ext)) {
    return `${name} was not added. Use ${SUPPORTED_FORMATS_LABEL}.`;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Primary action
// ---------------------------------------------------------------------------

export const START_SEARCH_LABEL = 'Start document review';
export const STARTING_SEARCH_LABEL = 'Starting document review…';

export type ExistingSearchTarget = 'review' | 'findings';

/** Where the primary action of an existing search goes. */
export function getExistingSearchTarget(job: WorkflowJob): ExistingSearchTarget {
  return isTitleReviewFinalized(job) ? 'findings' : 'review';
}

/** Primary action label for an existing search (Requirement 6.7 / design). */
export function getExistingSearchLabel(job: WorkflowJob): string {
  return getExistingSearchTarget(job) === 'findings' ? 'View findings' : 'Review extracted data';
}
